<template>
  <v-row>
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      :timeout="snackbarTimeout"
      :top="true"
    >
      {{snackbarMsg}}
      <v-btn dark text @click="showSnackbar = false">CLOSE</v-btn>
    </v-snackbar>

    <!-- Converted Currency list dialog -->
    <v-dialog v-model="dialogOnConvertedCurrencies" max-width="230">
      <v-row no-gutters>
        <v-col>
          <v-card color="white" flat v-if="selectedFCItem != null">
            <v-card-title class="headline primary--text">
              <v-row justify="center">
                <span>Convert to {{quotation.currency}}</span>
              </v-row>
            </v-card-title>

            <v-card-text>
              <v-row no-gutters>
                <v-col class="d-flex justify-content-start">
                  <span class="font-weight-bold body-2">Currency</span>
                </v-col>
                <v-col class="d-flex justify-content-end">
                  <span class="font-weight-bold body-2">Rate</span>
                </v-col>
              </v-row>
              <v-divider></v-divider>

              <template v-for="(item, i) in convertedCurrenciesList">
                <v-row
                  class="update-converted-currency py-2 px-2"
                  :key="i"
                  style="cursor: pointer;"
                  @click="updateFCItemConverted(item)"
                  no-gutters
                >
                  <v-col class="d-flex justify-content-start">
                    <span class="font-weight-bold caption">{{item.base_currency}}</span>
                  </v-col>

                  <v-col class="d-flex justify-content-end">
                    <span class="font-weight-bold caption">{{item.conversion_rate.toFixed(4)}}</span>
                  </v-col>
                </v-row>
                <v-divider class="py-0 my-0" :key="`${i}-d`"></v-divider>
              </template>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-dialog>

    <v-row no-gutters>
      <v-col v-if="fc_valid">
        <v-card flat color="white" class="d-flex justify-center">
          <v-card-text class="mb-2 pb-2 mt-1 pt-1">
            <v-row class="black--text">
              <table class="financial-table" style="width: 100%" v-if="fc != null">
                <!-- Header -->
                <tr>
                  <th class="action-cell"></th>
                  <th colspan="2">
                    <v-row justify="space-between" no-gutters>
                      <v-col cols="2">
                        <v-tooltip top>
                          <template v-slot:activator="{on}">
                            <v-btn
                              v-show="!portPrefixDisplayed"
                              v-on="on"
                              x-small
                              tile
                              depressed
                              color="secondary"
                              class="font-weight-bold caption mb-1 px-1 mr-3"
                              @click="prefixPortDescription"
                            >show port</v-btn>
                          </template>
                          Append the port code to the prefix
                        </v-tooltip>
                      </v-col>

                      <v-col cols="6">
                        <span class="font-weight-bold body-2">Description</span>
                      </v-col>

                      <v-col cols="2">
                        <v-tooltip top>
                          <template v-slot:activator="{on}">
                            <v-btn
                              v-show="!rateUnitSuffixDisplayed"
                              v-on="on"
                              x-small
                              tile
                              depressed
                              color="secondary"
                              class="font-weight-bold caption mb-1 px-1 ml-4"
                              @click="suffixRateUnitDescription"
                            >show rate/unit</v-btn>
                          </template>
                          Append the rate per unit to the suffix
                        </v-tooltip>
                      </v-col>

                      <v-col cols="2" class="d-flex justify-content-end">
                        <v-tooltip top>
                          <template v-slot:activator="{on}">
                            <v-btn
                              v-show="portPrefixDisplayed || rateUnitSuffixDisplayed"
                              class="mt-1"
                              v-on="on"
                              small
                              icon
                              color="error"
                              @click="resetDescription"
                            >
                              <v-icon size="25">mdi-restart</v-icon>
                            </v-btn>
                          </template>
                          Reset to the original description view
                        </v-tooltip>
                      </v-col>
                    </v-row>
                  </th>
                  <th v-if="allowEditComponent" style="width: 8%">
                    <span class="font-weight-bold">Code</span>
                  </th>
                  <th style="width: 8%">
                    <span class="font-weight-bold">Qty</span>
                  </th>
                  <th style="width: 8%">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <span class="font-weight-bold" v-on="on">B. PPU</span>
                      </template>
                      Price per unit in original currency
                    </v-tooltip>
                  </th>
                  <th style="width: 8%">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <span class="font-weight-bold" v-on="on">Conv.</span>
                      </template>
                      Conversion Factor
                    </v-tooltip>
                  </th>
                  <th style="width: 10%">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <span class="font-weight-bold" v-on="on">C. PPU ({{quotation.currency}})</span>
                      </template>
                      Price per unit after conversion to {{quotation.currency}}
                    </v-tooltip>
                  </th>
                  <th style="width: 10%">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <span class="font-weight-bold" v-on="on">Total ({{quotation.currency}})</span>
                      </template>
                      Total price after conversion to {{quotation.currency}}
                    </v-tooltip>
                  </th>
                </tr>

                <!-- Ocean (Sea) Freight charges -->
                <tbody
                  class="line-item-set"
                  v-if="fc.hasOwnProperty(CONSTANT.TYPE_OCEAN_FREIGHT_CHARGES)"
                >
                  <tr class="body-2 line-item-set-header">
                    <td>
                      <v-btn
                        x-small
                        icon
                        @click="showOceanFreightChargesItems = !showOceanFreightChargesItems"
                        style="background-color: gainsboro"
                      >
                        <v-icon>{{!showOceanFreightChargesItems ? "add" : "remove"}}</v-icon>
                      </v-btn>
                    </td>
                    <td class="description">
                      Freight charges {{quotation.origin_port.un_code + '-' + quotation.destination_port.un_code}}
                      <v-btn
                        x-small
                        icon
                        dark
                        class="ml-2 add-remove-item"
                        @click="onAddItem(CONSTANT.TYPE_OCEAN_FREIGHT_CHARGES, 'OT-GEN')"
                        v-if="allowEditComponent"
                        style="background-color: #1976D2"
                      >
                        <v-icon size="18" color="white">add</v-icon>
                      </v-btn>
                    </td>
                    <td></td>
                    <td v-if="allowEditComponent" class="input-cell"></td>
                    <td class="input-cell" colspan="4"></td>
                    <td class="input-cell font-weight-bold">
                      <input
                        class="input-number"
                        :placeholder="getComponentPrice(fc[CONSTANT.TYPE_OCEAN_FREIGHT_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        :value="getComponentPrice(fc[CONSTANT.TYPE_OCEAN_FREIGHT_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        readonly
                      />
                    </td>
                  </tr>

                  <template v-for="(item, i) in fc[CONSTANT.TYPE_OCEAN_FREIGHT_CHARGES]">
                    <tr
                      class="caption line-item-set-items"
                      v-show="showSubLineItem(showOceanFreightChargesItems, item) && (item.price.converted > 0.00 || $root.is_admin)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        <input
                          type="text"
                          class="input-description"
                          @input="handleInput"
                          :placeholder="item.description"
                          v-model="item.description"
                          :readonly="!allowEditComponent"
                        />
                        <v-btn
                          v-if="allowEditComponent"
                          x-small
                          icon
                          @click="onRemoveItem(item)"
                          class="ml-2 add-remove-item"
                          style="background-color: #FF5252"
                        >
                          <v-icon size="18" color="white">remove</v-icon>
                        </v-btn>
                      </td>
                      <td>
                        <v-chip
                          v-if="$root.is_admin"
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                        >{{item.currency}}</v-chip>

                        <v-chip
                          v-else
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                        >{{item.currency}}</v-chip>
                      </td>
                      <td v-if="allowEditComponent" class="input-cell">
                        <input
                          type="text"
                          class="caption"
                          @input="handleInput"
                          v-model="item.code"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput()"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.base.toFixed(2)"
                          v-model.number="item.price.base"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.conversion_factor.toFixed(4)"
                          v-model.number="item.conversion_factor"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          :value="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>
                </tbody>

                <!-- Air Freight charges -->
                <tbody
                  class="line-item-set"
                  v-if="fc.hasOwnProperty(CONSTANT.TYPE_AIR_FREIGHT_CHARGES)"
                >
                  <tr class="body-2 line-item-set-header">
                    <td>
                      <v-btn
                        x-small
                        icon
                        @click="showAirFreightChargesItems = !showAirFreightChargesItems"
                        style="background-color: gainsboro"
                      >
                        <v-icon>{{!showAirFreightChargesItems ? "add" : "remove"}}</v-icon>
                      </v-btn>
                    </td>
                    <td class="description">
                      Freight charges {{quotation.origin_port.un_code + '-' + quotation.destination_port.un_code}}
                      <v-btn
                        x-small
                        icon
                        dark
                        class="ml-2 add-remove-item"
                        @click="onAddItem(CONSTANT.TYPE_AIR_FREIGHT_CHARGES, 'OT-GEN')"
                        v-if="allowEditComponent"
                        style="background-color: #1976D2"
                      >
                        <v-icon size="18" color="white">add</v-icon>
                      </v-btn>
                    </td>
                    <td></td>
                    <td v-if="allowEditComponent" class="input-cell"></td>
                    <td class="input-cell" colspan="4"></td>
                    <td class="input-cell font-weight-bold">
                      <input
                        class="input-number"
                        :placeholder="getComponentPrice(fc[CONSTANT.TYPE_AIR_FREIGHT_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        :value="getComponentPrice(fc[CONSTANT.TYPE_AIR_FREIGHT_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        readonly
                      />
                    </td>
                  </tr>

                  <template v-for="(item, i) in fc[CONSTANT.TYPE_AIR_FREIGHT_CHARGES]">
                    <tr
                      class="caption line-item-set-items"
                      v-show="showSubLineItem(showAirFreightChargesItems, item) && (item.price.converted > 0.00 || $root.is_admin)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        <input
                          type="text"
                          class="input-description"
                          @input="handleInput"
                          :placeholder="item.description"
                          v-model="item.description"
                          :readonly="!allowEditComponent"
                        />
                        <v-btn
                          v-if="allowEditComponent"
                          x-small
                          icon
                          @click="onRemoveItem(item)"
                          class="ml-2 add-remove-item"
                          style="background-color: #FF5252"
                        >
                          <v-icon size="18" color="white">remove</v-icon>
                        </v-btn>
                      </td>
                      <td>
                        <v-chip
                          v-if="$root.is_admin"
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                        >{{item.currency}}</v-chip>

                        <v-chip
                          v-else
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                        >{{item.currency}}</v-chip>
                      </td>
                      <td v-if="allowEditComponent" class="input-cell">
                        <input
                          type="text"
                          class="caption"
                          @input="handleInput"
                          v-model="item.code"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput()"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.base.toFixed(2)"
                          v-model.number="item.price.base"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.conversion_factor.toFixed(4)"
                          v-model.number="item.conversion_factor"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          :value="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>
                </tbody>

                <!-- Insurance -->
                <tbody
                  class="line-item-set"
                  v-if="fc.hasOwnProperty(CONSTANT.TYPE_INSURANCE_CHARGES)"
                >
                  <tr class="body-2 line-item-set-header">
                    <td>
                      <v-btn
                        small
                        class="expand-icon"
                        icon
                        @click="showInsuranceItems = !showInsuranceItems"
                      >
                        <v-icon>{{!showInsuranceItems ? "add" : "remove"}}</v-icon>
                      </v-btn>
                    </td>
                    <td class="description">
                      Insurance
                      <v-btn
                        x-small
                        icon
                        dark
                        class="ml-2 add-remove-item"
                        @click="onAddItem(CONSTANT.TYPE_INSURANCE_CHARGES, 'OT-GEN')"
                        v-if="allowEditComponent"
                        style="background-color: #1976D2"
                      >
                        <v-icon size="18" color="white">add</v-icon>
                      </v-btn>
                    </td>
                    <td></td>
                    <td v-if="allowEditComponent" class="input-cell"></td>
                    <td class="input-cell" colspan="4"></td>
                    <td class="input-cell font-weight-bold">
                      <input
                        class="input-number"
                        :placeholder="getComponentPrice(fc[CONSTANT.TYPE_INSURANCE_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        :value="getComponentPrice(fc[CONSTANT.TYPE_INSURANCE_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        readonly
                      />
                    </td>
                  </tr>
                  <template v-for="(item, i) in fc[CONSTANT.TYPE_INSURANCE_CHARGES]">
                    <tr
                      class="caption line-item-set-items"
                      v-show="showSubLineItem(showInsuranceItems, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        <input
                          type="text"
                          class="input-description"
                          @input="handleInput"
                          :placeholder="item.description"
                          v-model="item.description"
                          :readonly="!allowEditComponent"
                        />
                        <v-btn
                          v-if="allowEditComponent"
                          x-small
                          icon
                          @click="onRemoveItem(item)"
                          class="ml-2 add-remove-item"
                          style="background-color: #FF5252"
                        >
                          <v-icon size="18" color="white">remove</v-icon>
                        </v-btn>
                      </td>
                      <td>
                        <v-chip
                          v-if="$root.is_admin"
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                        >{{item.currency}}</v-chip>

                        <v-chip
                          v-else
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                        >{{item.currency}}</v-chip>
                      </td>
                      <td v-if="allowEditComponent" class="input-cell">
                        <input
                          type="text"
                          class="caption"
                          @input="handleInput"
                          v-model="item.code"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.price.base"
                          :placeholder="item.price.base.toFixed(2)"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.conversion_factor.toFixed(4)"
                          v-model.number="item.conversion_factor"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          :value="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>
                </tbody>

                <!-- Export DG Class Charges -->
                <tbody
                  class="line-item-set"
                  v-if="fc.hasOwnProperty(CONSTANT.TYPE_EXPORT_DGCLASS_CHARGES)"
                >
                  <tr class="body-2 accentline-item-set-header">
                    <td>
                      <v-btn
                        x-small
                        icon
                        style="background-color: gainsboro"
                        @click="showExportCargoChargesTypeItems = !showExportCargoChargesTypeItems"
                      >
                        <v-icon>{{!showExportCargoChargesTypeItems ? "add" : "remove"}}</v-icon>
                      </v-btn>
                    </td>
                    <td class="description">
                      Export cargo specific charges at {{quotation.origin_port.name}}
                      <v-btn
                        x-small
                        icon
                        dark
                        class="ml-2 add-remove-item"
                        @click="onAddItem(CONSTANT.TYPE_EXPORT_DGCLASS_CHARGES, 'OT-GEN')"
                        v-if="allowEditComponent"
                        style="background-color: #1976D2"
                      >
                        <v-icon size="18" color="white">add</v-icon>
                      </v-btn>
                    </td>
                    <td></td>
                    <td v-if="allowEditComponent" class="input-cell"></td>
                    <td class="input-cell" colspan="4"></td>
                    <td class="input-cell font-weight-bold">
                      <input
                        class="input-number"
                        :placeholder="getComponentPrice(fc[CONSTANT.TYPE_EXPORT_DGCLASS_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        :value="getComponentPrice(fc[CONSTANT.TYPE_EXPORT_DGCLASS_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        readonly
                      />
                    </td>
                  </tr>
                  <template v-for="(item, i) in fc[CONSTANT.TYPE_EXPORT_DGCLASS_CHARGES]">
                    <tr
                      class="caption line-item-set-items"
                      v-show="showSubLineItem(showExportCargoChargesTypeItems, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        <input
                          type="text"
                          class="input-description"
                          @input="handleInput"
                          :placeholder="item.description"
                          v-model="item.description"
                          :readonly="!allowEditComponent"
                        />
                        <v-btn
                          v-if="allowEditComponent"
                          x-small
                          icon
                          @click="onRemoveItem(item)"
                          class="ml-2 add-remove-item"
                          style="background-color: #FF5252"
                        >
                          <v-icon size="18" color="white">remove</v-icon>
                        </v-btn>
                      </td>
                      <td>
                        <v-chip
                          v-if="$root.is_admin"
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                        >{{item.currency}}</v-chip>

                        <v-chip
                          v-else
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                        >{{item.currency}}</v-chip>
                      </td>
                      <td v-if="allowEditComponent" class="input-cell">
                        <input
                          type="text"
                          class="caption"
                          @input="handleInput"
                          v-model="item.code"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.price.base"
                          :placeholder="item.price.base.toFixed(2)"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.conversion_factor.toFixed(4)"
                          v-model.number="item.conversion_factor"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          :value="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>
                </tbody>

                <!-- Export haulage -->
                <tbody
                  class="line-item-set"
                  v-if="fc.hasOwnProperty(CONSTANT.TYPE_EXPORT_HAULAGE_CHARGES)"
                >
                  <tr class="body-2 line-item-set-header">
                    <td>
                      <v-btn
                        x-small
                        style="background-color: gainsboro"
                        icon
                        @click="showExportHaulageItems = !showExportHaulageItems"
                      >
                        <v-icon>{{!showExportHaulageItems ? "add" : "remove"}}</v-icon>
                      </v-btn>
                    </td>
                    <td class="description" colspan="2">
                      Total export haulage charges from {{quotation.city_haulage_export.name}}
                      <v-btn
                        x-small
                        icon
                        dark
                        class="ml-2 add-remove-item"
                        @click="onAddItem(CONSTANT.TYPE_EXPORT_HAULAGE_CHARGES, 'OT-GEN')"
                        v-if="allowEditComponent"
                        style="background-color: #1976D2"
                      >
                        <v-icon size="18" color="white">add</v-icon>
                      </v-btn>
                    </td>
                    <!-- <td></td> -->
                    <td v-if="allowEditComponent" class="input-cell"></td>
                    <td class="input-cell" colspan="4"></td>
                    <td class="input-cell font-weight-bold">
                      <input
                        class="input-number"
                        :placeholder="getComponentPrice(fc[CONSTANT.TYPE_EXPORT_HAULAGE_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        :value="getComponentPrice(fc[CONSTANT.TYPE_EXPORT_HAULAGE_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        readonly
                      />
                    </td>
                  </tr>
                  <template v-for="(item, i) in fc[CONSTANT.TYPE_EXPORT_HAULAGE_CHARGES]">
                    <tr
                      class="caption line-item-set-items"
                      v-show="showSubLineItem(showExportHaulageItems, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        <input
                          type="text"
                          class="input-description"
                          @input="handleInput"
                          :placeholder="item.description"
                          v-model="item.description"
                          :readonly="!allowEditComponent"
                        />
                        <v-btn
                          v-if="allowEditComponent"
                          x-small
                          icon
                          @click="onRemoveItem(item)"
                          class="ml-2 add-remove-item"
                          style="background-color: #FF5252"
                        >
                          <v-icon size="18" color="white">remove</v-icon>
                        </v-btn>
                      </td>
                      <td>
                        <v-chip
                          v-if="$root.is_admin"
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                        >{{item.currency}}</v-chip>

                        <v-chip
                          v-else
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                        >{{item.currency}}</v-chip>
                      </td>
                      <td v-if="allowEditComponent" class="input-cell">
                        <input
                          type="text"
                          class="caption"
                          @input="handleInput"
                          v-model="item.code"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.price.base"
                          :placeholder="item.price.base.toFixed(2)"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.conversion_factor.toFixed(4)"
                          v-model.number="item.conversion_factor"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          :value="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>
                </tbody>

                <!-- Export transport -->
                <tbody
                  class="line-item-set"
                  v-if="fc.hasOwnProperty(CONSTANT.TYPE_EXPORT_TRANSPORT_CHARGES)"
                >
                  <tr class="body-2 line-item-set-header">
                    <td>
                      <v-btn
                        x-small
                        style="background-color: gainsboro"
                        icon
                        @click="showExportTransportItems = !showExportTransportItems"
                      >
                        <v-icon>{{!showExportTransportItems ? "add" : "remove"}}</v-icon>
                      </v-btn>
                    </td>
                    <td class="description" colspan="2">
                      Export transport charges: {{quotation.city_transport_export.name + " - " + quotation.origin_port.name}}
                      <v-btn
                        x-small
                        icon
                        dark
                        class="ml-2 add-remove-item"
                        @click="onAddItem(CONSTANT.TYPE_EXPORT_TRANSPORT_CHARGES, 'OT-GEN')"
                        v-if="allowEditComponent"
                        style="background-color: #1976D2"
                      >
                        <v-icon size="18" color="white">add</v-icon>
                      </v-btn>
                    </td>
                    <!-- <td></td> -->
                    <td v-if="allowEditComponent" class="input-cell"></td>
                    <td class="input-cell" colspan="4"></td>
                    <td class="input-cell font-weight-bold">
                      <input
                        class="input-number"
                        :placeholder="getComponentPrice(fc[CONSTANT.TYPE_EXPORT_TRANSPORT_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        :value="getComponentPrice(fc[CONSTANT.TYPE_EXPORT_TRANSPORT_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        readonly
                      />
                    </td>
                  </tr>
                  <template v-for="(item, i) in fc[CONSTANT.TYPE_EXPORT_TRANSPORT_CHARGES]">
                    <tr
                      class="caption line-item-set-items"
                      v-show="showSubLineItem(showExportTransportItems, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        <input
                          type="text"
                          class="input-description"
                          @input="handleInput"
                          :placeholder="item.description"
                          v-model="item.description"
                          :readonly="!allowEditComponent"
                        />
                        <v-btn
                          v-if="allowEditComponent"
                          x-small
                          icon
                          @click="onRemoveItem(item)"
                          class="ml-2 add-remove-item"
                          style="background-color: #FF5252"
                        >
                          <v-icon size="18" color="white">remove</v-icon>
                        </v-btn>
                      </td>
                      <td>
                        <v-chip
                          v-if="$root.is_admin"
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                        >{{item.currency}}</v-chip>

                        <v-chip
                          v-else
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                        >{{item.currency}}</v-chip>
                      </td>
                      <td v-if="allowEditComponent" class="input-cell">
                        <input
                          type="text"
                          class="caption"
                          @input="handleInput"
                          v-model="item.code"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.price.base"
                          :placeholder="item.price.base.toFixed(2)"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.conversion_factor.toFixed(4)"
                          v-model.number="item.conversion_factor"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          :value="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>
                </tbody>

                <!-- Export Local charges -->
                <tbody
                  class="line-item-set"
                  v-if="fc.hasOwnProperty(CONSTANT.TYPE_EXPORT_LOCAL_CHARGES)"
                >
                  <tr class="body-2 line-item-set-header">
                    <td>
                      <v-btn
                        x-small
                        style="background-color: gainsboro"
                        icon
                        @click="showExportLocalChargesItems = !showExportLocalChargesItems"
                      >
                        <v-icon>{{!showExportLocalChargesItems ? "add" : "remove"}}</v-icon>
                      </v-btn>
                    </td>
                    <td class="description">
                      Export local charges at {{quotation.origin_port.name}}
                      <v-btn
                        x-small
                        icon
                        dark
                        class="ml-2 add-remove-item"
                        @click="onAddItem(CONSTANT.TYPE_EXPORT_LOCAL_CHARGES, 'OT-GEN')"
                        v-if="allowEditComponent"
                        style="background-color: #1976D2"
                      >
                        <v-icon size="18" color="white">add</v-icon>
                      </v-btn>
                    </td>
                    <td></td>
                    <td v-if="allowEditComponent" class="input-cell"></td>
                    <td class="input-cell" colspan="4"></td>
                    <td class="input-cell font-weight-bold">
                      <input
                        class="input-number"
                        :placeholder="getComponentPrice(fc[CONSTANT.TYPE_EXPORT_LOCAL_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        :value="getComponentPrice(fc[CONSTANT.TYPE_EXPORT_LOCAL_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        readonly
                      />
                    </td>
                  </tr>
                  <template v-for="(item, i) in fc[CONSTANT.TYPE_EXPORT_LOCAL_CHARGES]">
                    <tr
                      class="caption line-item-set-items"
                      v-show="showSubLineItem(showExportLocalChargesItems, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        <input
                          type="text"
                          class="input-description"
                          @input="handleInput"
                          :placeholder="item.description"
                          v-model="item.description"
                          :readonly="!allowEditComponent"
                        />
                        <v-btn
                          v-if="allowEditComponent"
                          x-small
                          icon
                          @click="onRemoveItem(item)"
                          class="ml-2 add-remove-item"
                          style="background-color: #FF5252"
                        >
                          <v-icon size="18" color="white">remove</v-icon>
                        </v-btn>
                      </td>
                      <td>
                        <v-chip
                          v-if="$root.is_admin"
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                        >{{item.currency}}</v-chip>

                        <v-chip
                          v-else
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                        >{{item.currency}}</v-chip>
                      </td>
                      <td v-if="allowEditComponent" class="input-cell">
                        <input
                          type="text"
                          class="caption"
                          @input="handleInput"
                          v-model="item.code"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.price.base"
                          :placeholder="item.price.base.toFixed(2)"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.conversion_factor.toFixed(4)"
                          v-model.number="item.conversion_factor"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          :value="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>
                </tbody>

                <!-- Export customs clearance -->
                <tbody
                  class="line-item-set"
                  v-if="fc.hasOwnProperty(CONSTANT.TYPE_EXPORT_CUSTOMS_CHARGES)"
                >
                  <tr class="body-2 line-item-set-header">
                    <td>
                      <v-btn
                        x-small
                        style="background-color: gainsboro"
                        icon
                        @click="showExportCustomsClearanceItems = !showExportCustomsClearanceItems"
                      >
                        <v-icon>{{!showExportCustomsClearanceItems ? "add" : "remove"}}</v-icon>
                      </v-btn>
                    </td>
                    <td class="description">
                      Export customs clearance at {{quotation.origin_port.name}}
                      <v-btn
                        x-small
                        icon
                        dark
                        class="ml-2 add-remove-item"
                        @click="onAddItem(CONSTANT.TYPE_EXPORT_CUSTOMS_CHARGES, 'OT-GEN')"
                        v-if="allowEditComponent"
                        style="background-color: #1976D2"
                      >
                        <v-icon size="18" color="white">add</v-icon>
                      </v-btn>
                    </td>
                    <td></td>
                    <td v-if="allowEditComponent" class="input-cell"></td>
                    <td class="input-cell" colspan="4"></td>
                    <td class="input-cell font-weight-bold">
                      <input
                        class="input-number"
                        :placeholder="getComponentPrice(fc[CONSTANT.TYPE_EXPORT_CUSTOMS_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        :value="getComponentPrice(fc[CONSTANT.TYPE_EXPORT_CUSTOMS_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        @input="handleInput"
                        readonly
                      />
                    </td>
                  </tr>
                  <template v-for="(item, i) in fc[CONSTANT.TYPE_EXPORT_CUSTOMS_CHARGES]">
                    <tr
                      class="caption line-item-set-items"
                      v-show="showSubLineItem(showExportCustomsClearanceItems, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        <input
                          type="text"
                          class="input-description"
                          @input="handleInput"
                          :placeholder="item.description"
                          v-model="item.description"
                          :readonly="!allowEditComponent"
                        />
                        <v-btn
                          v-if="allowEditComponent"
                          x-small
                          icon
                          @click="onRemoveItem(item)"
                          class="ml-2 add-remove-item"
                          style="background-color: #FF5252"
                        >
                          <v-icon size="18" color="white">remove</v-icon>
                        </v-btn>
                      </td>
                      <td>
                        <v-chip
                          v-if="$root.is_admin"
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                        >{{item.currency}}</v-chip>

                        <v-chip
                          v-else
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                        >{{item.currency}}</v-chip>
                      </td>
                      <td v-if="allowEditComponent" class="input-cell">
                        <input
                          type="text"
                          class="caption"
                          @input="handleInput"
                          v-model="item.code"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.price.base"
                          :placeholder="item.price.base.toFixed(2)"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.conversion_factor.toFixed(4)"
                          v-model.number="item.conversion_factor"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          :value="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>
                </tbody>

                <!-- Import DG Class Charges -->
                <tbody
                  class="line-item-set"
                  v-if="fc.hasOwnProperty(CONSTANT.TYPE_IMPORT_DGCLASS_CHARGES)"
                >
                  <tr class="body-2 line-item-set-header">
                    <td>
                      <v-btn
                        x-small
                        style="background-color: gainsboro"
                        icon
                        @click="showImportCargoChargesTypeItems = !showImportCargoChargesTypeItems"
                      >
                        <v-icon>{{!showImportCargoChargesTypeItems ? "add" : "remove"}}</v-icon>
                      </v-btn>
                    </td>
                    <td class="description">
                      Import cargo specific charges at {{quotation.destination_port.name}}
                      <v-btn
                        x-small
                        icon
                        dark
                        class="ml-2 add-remove-item"
                        @click="onAddItem(CONSTANT.TYPE_IMPORT_DGCLASS_CHARGES, 'OT-GEN')"
                        v-if="allowEditComponent"
                        style="background-color: #1976D2"
                      >
                        <v-icon size="18" color="white">add</v-icon>
                      </v-btn>
                    </td>
                    <td></td>
                    <td v-if="allowEditComponent" class="input-cell"></td>
                    <td class="input-cell" colspan="4"></td>
                    <td class="input-cell font-weight-bold">
                      <input
                        class="input-number"
                        :value="getComponentPrice(fc[CONSTANT.TYPE_IMPORT_DGCLASS_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        :placeholder="getComponentPrice(fc[CONSTANT.TYPE_IMPORT_DGCLASS_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        readonly
                      />
                    </td>
                  </tr>
                  <template v-for="(item, i) in fc[CONSTANT.TYPE_IMPORT_DGCLASS_CHARGES]">
                    <tr
                      class="caption line-item-set-items"
                      v-show="showSubLineItem(showImportCargoChargesTypeItems, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        <input
                          type="text"
                          class="input-description"
                          @input="handleInput"
                          :placeholder="item.description"
                          v-model="item.description"
                          :readonly="!allowEditComponent"
                        />
                        <v-btn
                          v-if="allowEditComponent"
                          x-small
                          icon
                          @click="onRemoveItem(item)"
                          class="ml-2 add-remove-item"
                          style="background-color: #FF5252"
                        >
                          <v-icon size="18" color="white">remove</v-icon>
                        </v-btn>
                      </td>
                      <td>
                        <v-chip
                          v-if="$root.is_admin"
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                        >{{item.currency}}</v-chip>

                        <v-chip
                          v-else
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                        >{{item.currency}}</v-chip>
                      </td>
                      <td v-if="allowEditComponent" class="input-cell">
                        <input
                          type="text"
                          class="caption"
                          @input="handleInput"
                          v-model="item.code"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.price.base"
                          :placeholder="item.price.base.toFixed(2)"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.conversion_factor.toFixed(4)"
                          v-model.number="item.conversion_factor"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          :value="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>
                </tbody>

                <!-- Import customs clearance -->
                <tbody
                  class="line-item-set"
                  v-if="fc.hasOwnProperty(CONSTANT.TYPE_IMPORT_CUSTOMS_CHARGES)"
                >
                  <tr class="body-2 line-item-set-header">
                    <td>
                      <v-btn
                        x-small
                        style="background-color: gainsboro"
                        icon
                        @click="showImportCustomsClearanceItems = !showImportCustomsClearanceItems"
                      >
                        <v-icon>{{!showImportCustomsClearanceItems ? "add" : "remove"}}</v-icon>
                      </v-btn>
                    </td>
                    <td class="description">
                      Import customs clearance at {{quotation.destination_port.name}}
                      <v-btn
                        x-small
                        icon
                        dark
                        class="ml-2 add-remove-item"
                        @click="onAddItem(CONSTANT.TYPE_IMPORT_CUSTOMS_CHARGES, 'OT-GEN')"
                        v-if="allowEditComponent"
                        style="background-color: #1976D2"
                      >
                        <v-icon size="18" color="white">add</v-icon>
                      </v-btn>
                    </td>
                    <td></td>
                    <td v-if="allowEditComponent" class="input-cell"></td>
                    <td class="input-cell" colspan="4"></td>
                    <td class="input-cell font-weight-bold">
                      <input
                        class="input-number"
                        :placeholder="getComponentPrice(fc[CONSTANT.TYPE_IMPORT_CUSTOMS_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        :value="getComponentPrice(fc[CONSTANT.TYPE_IMPORT_CUSTOMS_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        @input="handleInput"
                        readonly
                      />
                    </td>
                  </tr>

                  <template v-for="(item, i) in fc[CONSTANT.TYPE_IMPORT_CUSTOMS_CHARGES]">
                    <tr
                      class="caption line-item-set-items"
                      v-show="showSubLineItem(showImportCustomsClearanceItems, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        <input
                          type="text"
                          class="input-description"
                          @input="handleInput"
                          :placeholder="item.description"
                          v-model="item.description"
                          :readonly="!allowEditComponent"
                        />
                        <v-btn
                          v-if="allowEditComponent"
                          x-small
                          icon
                          @click="onRemoveItem(item)"
                          class="ml-2 add-remove-item"
                          style="background-color: #FF5252"
                        >
                          <v-icon size="18" color="white">remove</v-icon>
                        </v-btn>
                      </td>
                      <td>
                        <v-chip
                          v-if="$root.is_admin"
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                        >{{item.currency}}</v-chip>
                        <v-chip
                          v-else
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                        >{{item.currency}}</v-chip>
                      </td>
                      <td v-if="allowEditComponent" class="input-cell">
                        <input
                          type="text"
                          class="caption"
                          @input="handleInput"
                          v-model="item.code"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.price.base"
                          :placeholder="item.price.base.toFixed(2)"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.conversion_factor.toFixed(4)"
                          v-model.number="item.conversion_factor"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          :value="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>
                </tbody>

                <!-- Import Local charges -->
                <tbody
                  class="line-item-set"
                  v-if="fc.hasOwnProperty(CONSTANT.TYPE_IMPORT_LOCAL_CHARGES)"
                >
                  <tr class="body-2 line-item-set-header">
                    <td>
                      <v-btn
                        x-small
                        style="background-color: gainsboro"
                        icon
                        @click="showImportLocalChargesItems = !showImportLocalChargesItems"
                      >
                        <v-icon>{{!showImportLocalChargesItems ? "add" : "remove"}}</v-icon>
                      </v-btn>
                    </td>
                    <td class="description">
                      Import local charges at {{quotation.destination_port.name}}
                      <v-btn
                        x-small
                        icon
                        dark
                        class="ml-2 add-remove-item"
                        @click="onAddItem(CONSTANT.TYPE_IMPORT_LOCAL_CHARGES, 'OT-GEN')"
                        v-if="allowEditComponent"
                        style="background-color: #1976D2"
                      >
                        <v-icon size="18" color="white">add</v-icon>
                      </v-btn>
                    </td>
                    <td></td>
                    <td v-if="allowEditComponent" class="input-cell"></td>
                    <td class="input-cell" colspan="4"></td>
                    <td class="input-cell font-weight-bold">
                      <input
                        class="input-number"
                        :placeholder="getComponentPrice(fc[CONSTANT.TYPE_IMPORT_LOCAL_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        :value="getComponentPrice(fc[CONSTANT.TYPE_IMPORT_LOCAL_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        readonly
                      />
                    </td>
                  </tr>
                  <template v-for="(item, i) in fc[CONSTANT.TYPE_IMPORT_LOCAL_CHARGES]">
                    <tr
                      class="caption line-item-set-items"
                      v-show="showSubLineItem(showImportLocalChargesItems, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        <input
                          type="text"
                          class="input-description"
                          @input="handleInput"
                          :placeholder="item.description"
                          v-model="item.description"
                          :readonly="!allowEditComponent"
                        />
                        <v-btn
                          v-if="allowEditComponent"
                          x-small
                          icon
                          @click="onRemoveItem(item)"
                          class="ml-2 add-remove-item"
                          style="background-color: #FF5252"
                        >
                          <v-icon size="18" color="white">remove</v-icon>
                        </v-btn>
                      </td>
                      <td>
                        <v-chip
                          v-if="$root.is_admin"
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                        >{{item.currency}}</v-chip>
                        <v-chip
                          v-else
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                        >{{item.currency}}</v-chip>
                      </td>
                      <td v-if="allowEditComponent" class="input-cell">
                        <input
                          type="text"
                          class="caption"
                          @input="handleInput"
                          v-model="item.code"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.price.base"
                          :placeholder="item.price.base.toFixed(2)"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.conversion_factor.toFixed(4)"
                          v-model.number="item.conversion_factor"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          :value="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>
                </tbody>

                <!-- Import haulage -->
                <tbody
                  class="line-item-set"
                  v-if="fc.hasOwnProperty(CONSTANT.TYPE_IMPORT_HAULAGE_CHARGES)"
                >
                  <tr class="body-2 line-item-set-header">
                    <td>
                      <v-btn
                        x-small
                        style="background-color: gainsboro"
                        icon
                        @click="showImportHaulageItems = !showImportHaulageItems"
                      >
                        <v-icon>{{!showImportHaulageItems ? "add" : "remove"}}</v-icon>
                      </v-btn>
                    </td>
                    <td class="description" colspan="2">
                      Total import haulage charges to {{quotation.city_haulage_import.name}}
                      <v-btn
                        x-small
                        icon
                        dark
                        class="ml-2 add-remove-item"
                        @click="onAddItem(CONSTANT.TYPE_IMPORT_HAULAGE_CHARGES, 'OT-GEN')"
                        v-if="allowEditComponent"
                        style="background-color: #1976D2"
                      >
                        <v-icon size="18" color="white">add</v-icon>
                      </v-btn>
                    </td>
                    <!-- <td></td> -->
                    <td v-if="allowEditComponent" class="input-cell"></td>
                    <td class="input-cell" colspan="4"></td>
                    <td class="input-cell font-weight-bold">
                      <input
                        class="input-number input-number-total"
                        :value="getComponentPrice(fc[CONSTANT.TYPE_IMPORT_HAULAGE_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        :placeholder="getComponentPrice(fc[CONSTANT.TYPE_IMPORT_HAULAGE_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        @input="handleInput"
                        readonly
                      />
                    </td>
                  </tr>
                  <template v-for="(item, i) in fc[CONSTANT.TYPE_IMPORT_HAULAGE_CHARGES]">
                    <tr
                      class="caption line-item-set-items"
                      v-show="showSubLineItem(showImportHaulageItems, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        <input
                          type="text"
                          class="input-description"
                          @input="handleInput"
                          :placeholder="item.description"
                          v-model="item.description"
                          :readonly="!allowEditComponent"
                        />
                        <v-btn
                          v-if="allowEditComponent"
                          x-small
                          icon
                          @click="onRemoveItem(item)"
                          class="ml-2 add-remove-item"
                          style="background-color: #FF5252"
                        >
                          <v-icon size="18" color="white">remove</v-icon>
                        </v-btn>
                      </td>
                      <td>
                        <v-chip
                          v-if="$root.is_admin"
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                        >{{item.currency}}</v-chip>

                        <v-chip
                          v-else
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                        >{{item.currency}}</v-chip>
                      </td>
                      <td v-if="allowEditComponent" class="input-cell">
                        <input
                          type="text"
                          class="caption"
                          @input="handleInput"
                          v-model="item.code"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.price.base"
                          :placeholder="item.price.base.toFixed(2)"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.conversion_factor.toFixed(4)"
                          v-model.number="item.conversion_factor"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          :value="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>
                </tbody>

                <!-- Import transport -->
                <tbody
                  class="line-item-set"
                  v-if="fc.hasOwnProperty(CONSTANT.TYPE_IMPORT_TRANSPORT_CHARGES)"
                >
                  <tr class="body-2 line-item-set-header">
                    <td>
                      <v-btn
                        x-small
                        style="background-color: gainsboro"
                        icon
                        @click="showImportTransportItems = !showImportTransportItems"
                      >
                        <v-icon>{{!showImportTransportItems ? "add" : "remove"}}</v-icon>
                      </v-btn>
                    </td>
                    <td class="description" colspan="2">
                      Import transport charges: {{quotation.destination_port.name + " - " + quotation.city_transport_import.name}}
                      <v-btn
                        x-small
                        icon
                        dark
                        class="ml-2 add-remove-item"
                        @click="onAddItem(CONSTANT.TYPE_IMPORT_TRANSPORT_CHARGES, 'OT-GEN')"
                        v-if="allowEditComponent"
                        style="background-color: #1976D2"
                      >
                        <v-icon size="18" color="white">add</v-icon>
                      </v-btn>
                    </td>
                    <!-- <td></td> -->
                    <td v-if="allowEditComponent" class="input-cell"></td>
                    <td class="input-cell" colspan="4"></td>
                    <td class="input-cell font-weight-bold">
                      <input
                        class="input-number"
                        :placeholder="getComponentPrice(fc[CONSTANT.TYPE_IMPORT_TRANSPORT_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        :value="getComponentPrice(fc[CONSTANT.TYPE_IMPORT_TRANSPORT_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        readonly
                      />
                    </td>
                  </tr>
                  <template v-for="(item, i) in fc[CONSTANT.TYPE_IMPORT_TRANSPORT_CHARGES]">
                    <tr
                      class="caption line-item-set-items"
                      v-show="showSubLineItem(showImportTransportItems, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        <input
                          type="text"
                          class="input-description"
                          @input="handleInput"
                          :placeholder="item.description"
                          v-model="item.description"
                          :readonly="!allowEditComponent"
                        />
                        <v-btn
                          v-if="allowEditComponent"
                          x-small
                          icon
                          @click="onRemoveItem(item)"
                          class="ml-2 add-remove-item"
                          style="background-color: #FF5252"
                        >
                          <v-icon size="18" color="white">remove</v-icon>
                        </v-btn>
                      </td>
                      <td>
                        <v-chip
                          v-if="$root.is_admin"
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                        >{{item.currency}}</v-chip>

                        <v-chip
                          v-else
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                        >{{item.currency}}</v-chip>
                      </td>
                      <td v-if="allowEditComponent" class="input-cell">
                        <input
                          type="text"
                          class="caption"
                          @input="handleInput"
                          v-model="item.code"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.price.base"
                          :placeholder="item.price.base.toFixed(2)"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.conversion_factor.toFixed(4)"
                          v-model.number="item.conversion_factor"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          :value="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>
                </tbody>

                <!-- Other services -->
                <tbody
                  class="line-item-set"
                  v-if="fc.hasOwnProperty(CONSTANT.TYPE_OTHER_SERVICES_CHARGES)"
                >
                  <tr class="body-2 line-item-set-header">
                    <td>
                      <v-btn
                        x-small
                        style="background-color: gainsboro"
                        icon
                        @click="showOtherServices = !showOtherServices"
                      >
                        <v-icon>{{!showOtherServices ? "add" : "remove"}}</v-icon>
                      </v-btn>
                    </td>
                    <td class="description">
                      <span>Other items/services &nbsp;&nbsp;&nbsp;</span>
                      <v-btn
                        x-small
                        icon
                        dark
                        class="ml-2"
                        @click="onAddItem(CONSTANT.TYPE_OTHER_SERVICES_CHARGES, 'OT-SER')"
                        v-if="allowEditComponent"
                        style="background-color: #1976D2"
                      >
                        <v-icon size="20" color="white">add</v-icon>
                      </v-btn>
                    </td>
                    <td></td>
                    <td v-if="allowEditComponent" class="input-cell"></td>
                    <td class="input-cell" colspan="4"></td>
                    <td class="input-cell font-weight-bold">
                      <input
                        class="input-number"
                        :placeholder="getComponentPrice(fc[CONSTANT.TYPE_OTHER_SERVICES_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        :value="getComponentPrice(fc[CONSTANT.TYPE_OTHER_SERVICES_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        readonly
                      />
                    </td>
                  </tr>
                  <template v-for="(item, i) in fc[CONSTANT.TYPE_OTHER_SERVICES_CHARGES]">
                    <tr
                      class="caption line-item-set-items"
                      v-show="showSubLineItem(showOtherServices, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        <input
                          type="text"
                          class="input-description"
                          @input="handleInput"
                          :placeholder="item.description"
                          v-model="item.description"
                          :readonly="!allowEditComponent"
                        />
                        <v-btn
                          v-if="allowEditComponent"
                          x-small
                          icon
                          @click="onRemoveItem(item)"
                          style="background-color: #FF5252"
                          class="add-remove-item"
                        >
                          <v-icon size="18" color="white">remove</v-icon>
                        </v-btn>
                      </td>

                      <td>
                        <v-chip
                          v-if="$root.is_admin"
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                        >{{item.currency}}</v-chip>

                        <v-chip
                          v-else
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                        >{{item.currency}}</v-chip>
                      </td>
                      <td v-if="allowEditComponent" class="input-cell">
                        <input
                          type="text"
                          class="caption"
                          @input="handleInput"
                          v-model="item.code"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.price.base"
                          :placeholder="item.price.base.toFixed(2)"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.conversion_factor.toFixed(4)"
                          v-model.number="item.conversion_factor"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          :value="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>
                </tbody>

                <!-- Handling fee -->
                <tbody
                  class="line-item-single"
                  v-if="fc.hasOwnProperty(CONSTANT.TYPE_HANDLING_CHARGES)"
                >
                  <tr class="body-2" v-for="(item, i) in fc[CONSTANT.TYPE_HANDLING_CHARGES]">
                    <td></td>
                    <td class="description">
                      <input
                        type="text"
                        class="input-description"
                        @input="handleInput"
                        :placeholder="item.description"
                        v-model="item.description"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td>
                      <v-chip
                        v-if="$root.is_admin"
                        x-small
                        label
                        dark
                        :color="getCurrencyColor(item).color"
                        class="font-weight-bold caption px-1"
                        @click="onOpenConvertedcurrenciesDialog(item)"
                      >{{item.currency}}</v-chip>

                      <v-chip
                        v-else
                        x-small
                        label
                        dark
                        :color="getCurrencyColor(item).color"
                        class="font-weight-bold caption px-1"
                      >{{item.currency}}</v-chip>
                    </td>
                    <td v-if="allowEditComponent" class="input-cell">
                      <input
                        type="text"
                        class="caption"
                        @input="handleInput"
                        v-model="item.code"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td class="input-cell">
                      <input
                        type="number"
                        class="input-number"
                        :placeholder="item.quantity"
                        v-model.number="item.quantity"
                        @input="handleInput"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td class="input-cell">
                      <input
                        type="number"
                        class="input-number"
                        v-model.number="item.price.base"
                        :placeholder="item.price.base.toFixed(2)"
                        @input="handleBaseRateInput(item.price, item.conversion_factor)"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td class="input-cell">
                      <input
                        type="number"
                        class="input-number"
                        :placeholder="item.conversion_factor.toFixed(4)"
                        v-model.number="item.conversion_factor"
                        @input="handleBaseRateInput(item.price, item.conversion_factor)"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td class="input-cell">
                      <input
                        type="number"
                        class="input-number"
                        :placeholder="item.price.converted.toFixed(2)"
                        :value="item.price.converted.toFixed(2)"
                        readonly
                      />
                    </td>
                    <td class="input-cell font-weight-bold">
                      <input
                        class="input-number"
                        :placeholder="getTotalRate(item.quantity, item.price.converted).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        :value="getTotalRate(item.quantity, item.price.converted).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        readonly
                      />
                    </td>
                  </tr>
                </tbody>

                <!-- Storage charges -->
                <tbody
                  class="line-item-single"
                  v-if="fc.hasOwnProperty(CONSTANT.TYPE_STORAGE_CHARGES)"
                >
                  <tr class="body-2" v-for="(item, i) in fc[CONSTANT.TYPE_STORAGE_CHARGES]">
                    <td></td>
                    <td class="description">
                      <input
                        type="text"
                        class="input-description"
                        @input="handleInput"
                        :placeholder="item.description"
                        v-model="item.description"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td>
                      <v-chip
                        v-if="$root.is_admin"
                        x-small
                        label
                        dark
                        :color="getCurrencyColor(item).color"
                        class="font-weight-bold caption px-1"
                        @click="onOpenConvertedcurrenciesDialog(item)"
                      >{{item.currency}}</v-chip>

                      <v-chip
                        v-else
                        x-small
                        label
                        dark
                        :color="getCurrencyColor(item).color"
                        class="font-weight-bold caption px-1"
                      >{{item.currency}}</v-chip>
                    </td>
                    <td v-if="allowEditComponent" class="input-cell">
                      <input
                        type="text"
                        class="caption"
                        @input="handleInput"
                        v-model="item.code"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td class="input-cell">
                      <input
                        type="number"
                        class="input-number"
                        :placeholder="item.quantity"
                        v-model.number="item.quantity"
                        @input="handleInput"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td class="input-cell">
                      <input
                        type="number"
                        class="input-number"
                        v-model.number="item.price.base"
                        :placeholder="item.price.base.toFixed(2)"
                        @input="handleBaseRateInput(item.price, item.conversion_factor)"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td class="input-cell">
                      <input
                        type="number"
                        class="input-number"
                        :placeholder="item.conversion_factor.toFixed(4)"
                        v-model.number="item.conversion_factor"
                        @input="handleBaseRateInput(item.price, item.conversion_factor)"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td class="input-cell">
                      <input
                        type="number"
                        class="input-number"
                        :placeholder="item.price.converted.toFixed(2)"
                        :value="item.price.converted.toFixed(2)"
                        readonly
                      />
                    </td>
                    <td class="input-cell font-weight-bold">
                      <input
                        class="input-number"
                        :placeholder="getTotalRate(item.quantity, item.price.converted).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        :value="getTotalRate(item.quantity, item.price.converted).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        readonly
                      />
                    </td>
                  </tr>
                </tbody>

                 <!-- Removal charges -->
                <tbody
                  class="line-item-single"
                  v-if="fc.hasOwnProperty(CONSTANT.TYPE_REMOVAL_CHARGES)"
                >
                  <tr class="body-2" v-for="(item, i) in fc[CONSTANT.TYPE_REMOVAL_CHARGES]">
                    <td></td>
                    <td class="description">
                      <input
                        type="text"
                        class="input-description"
                        @input="handleInput"
                        :placeholder="item.description"
                        v-model="item.description"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td>
                      <v-chip
                        v-if="$root.is_admin"
                        x-small
                        label
                        dark
                        :color="getCurrencyColor(item).color"
                        class="font-weight-bold caption px-1"
                        @click="onOpenConvertedcurrenciesDialog(item)"
                      >{{item.currency}}</v-chip>

                      <v-chip
                        v-else
                        x-small
                        label
                        dark
                        :color="getCurrencyColor(item).color"
                        class="font-weight-bold caption px-1"
                      >{{item.currency}}</v-chip>
                    </td>
                    <td v-if="allowEditComponent" class="input-cell">
                      <input
                        type="text"
                        class="caption"
                        @input="handleInput"
                        v-model="item.code"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td class="input-cell">
                      <input
                        type="number"
                        class="input-number"
                        :placeholder="item.quantity"
                        v-model.number="item.quantity"
                        @input="handleInput"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td class="input-cell">
                      <input
                        type="number"
                        class="input-number"
                        v-model.number="item.price.base"
                        :placeholder="item.price.base.toFixed(2)"
                        @input="handleBaseRateInput(item.price, item.conversion_factor)"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td class="input-cell">
                      <input
                        type="number"
                        class="input-number"
                        :placeholder="item.conversion_factor.toFixed(4)"
                        v-model.number="item.conversion_factor"
                        @input="handleBaseRateInput(item.price, item.conversion_factor)"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td class="input-cell">
                      <input
                        type="number"
                        class="input-number"
                        :placeholder="item.price.converted.toFixed(2)"
                        :value="item.price.converted.toFixed(2)"
                        readonly
                      />
                    </td>
                    <td class="input-cell font-weight-bold">
                      <input
                        class="input-number"
                        :placeholder="getTotalRate(item.quantity, item.price.converted).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        :value="getTotalRate(item.quantity, item.price.converted).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        readonly
                      />
                    </td>
                  </tr>
                </tbody>

                <!-- Taxes & Duties -->
                <tbody
                  class="line-item-set"
                  v-if="fc.hasOwnProperty(CONSTANT.TYPE_TAXES_DUTIES_CHARGES)"
                >
                  <tr class="body-2 line-item-set-header">
                    <td>
                      <v-btn
                        x-small
                        style="background-color: gainsboro"
                        icon
                        @click="showTaxesDuties = !showTaxesDuties"
                      >
                        <v-icon>{{!showTaxesDuties ? "add" : "remove"}}</v-icon>
                      </v-btn>
                    </td>
                    <td class="description">
                      <span>Taxes & Duties &nbsp;&nbsp;&nbsp;</span>
                      <v-btn
                        x-small
                        icon
                        dark
                        class="ml-2"
                        @click="onAddItem(CONSTANT.TYPE_TAXES_DUTIES_CHARGES, 'OT-GEN')"
                        v-if="allowEditComponent"
                        style="background-color: #1976D2"
                      >
                        <v-icon size="20" color="white">add</v-icon>
                      </v-btn>
                    </td>
                    <td></td>
                    <td v-if="allowEditComponent" class="input-cell"></td>
                    <td class="input-cell" colspan="4"></td>
                    <td class="input-cell font-weight-bold">
                      <input
                        class="input-number"
                        :placeholder="getComponentPrice(fc[CONSTANT.TYPE_TAXES_DUTIES_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        :value="getComponentPrice(fc[CONSTANT.TYPE_TAXES_DUTIES_CHARGES]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        readonly
                      />
                    </td>
                  </tr>
                  <template v-for="(item, i) in fc[CONSTANT.TYPE_TAXES_DUTIES_CHARGES]">
                    <tr
                      class="caption line-item-set-items"
                      v-show="showSubLineItem(showTaxesDuties, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        <input
                          type="text"
                          class="input-description"
                          @input="handleInput"
                          :placeholder="item.description"
                          v-model="item.description"
                          :readonly="!allowEditComponent"
                        />
                        <v-btn
                          v-if="allowEditComponent"
                          x-small
                          icon
                          @click="onRemoveItem(item)"
                          class="ml-2 add-remove-item"
                          style="background-color: #FF5252"
                        >
                          <v-icon size="18" color="white">remove</v-icon>
                        </v-btn>
                      </td>
                      <td>
                        <v-chip
                          v-if="$root.is_admin"
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                        >{{item.currency}}</v-chip>

                        <v-chip
                          v-else
                          x-small
                          label
                          dark
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                        >{{item.currency}}</v-chip>
                      </td>
                      <td v-if="allowEditComponent" class="input-cell">
                        <input
                          type="text"
                          class="caption"
                          @input="handleInput"
                          v-model="item.code"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.price.base"
                          :placeholder="item.price.base.toFixed(2)"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.conversion_factor.toFixed(4)"
                          v-model.number="item.conversion_factor"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!allowEditComponent"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          :value="getTotalRate(item.quantity, item.price.converted).toFixed(2)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>
                </tbody>

                <!-- General items -->
                <tbody
                  class="line-item-single"
                  v-if="fc.hasOwnProperty(CONSTANT.TYPE_GENERAL_ITEM)"
                >
                  <tr class="body-2" v-for="(item, i) in fc[CONSTANT.TYPE_GENERAL_ITEM]">
                    <td></td>
                    <td class="description">
                      <input
                        type="text"
                        class="input-description"
                        @input="handleInput"
                        :placeholder="item.description"
                        v-model="item.description"
                        :readonly="!allowEditComponent"
                      />

                      <v-btn
                        x-small
                        icon
                        @click="onRemoveItem(item)"
                        style="background-color: #FF5252"
                        class="add-remove-item"
                      >
                        <v-icon size="18" color="white">remove</v-icon>
                      </v-btn>
                    </td>
                    <td>
                      <v-chip
                        v-if="$root.is_admin"
                        x-small
                        label
                        dark
                        :color="getCurrencyColor(item).color"
                        class="font-weight-bold caption px-1"
                        @click="onOpenConvertedcurrenciesDialog(item)"
                      >{{item.currency}}</v-chip>

                      <v-chip
                        v-else
                        x-small
                        label
                        dark
                        :color="getCurrencyColor(item).color"
                        class="font-weight-bold caption px-1"
                      >{{item.currency}}</v-chip>
                    </td>
                    <td v-if="allowEditComponent" class="input-cell">
                      <input
                        type="text"
                        class="caption"
                        @input="handleInput"
                        v-model="item.code"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td class="input-cell">
                      <input
                        type="number"
                        class="input-number"
                        :placeholder="item.quantity"
                        v-model.number="item.quantity"
                        @input="handleInput"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td class="input-cell">
                      <input
                        type="number"
                        class="input-number"
                        v-model.number="item.price.base"
                        :placeholder="item.price.base.toFixed(2)"
                        @input="handleBaseRateInput(item.price, item.conversion_factor)"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td class="input-cell">
                      <input
                        type="number"
                        class="input-number"
                        :placeholder="item.conversion_factor.toFixed(4)"
                        v-model.number="item.conversion_factor"
                        @input="handleBaseRateInput(item.price, item.conversion_factor)"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td class="input-cell">
                      <input
                        type="number"
                        class="input-number"
                        :placeholder="item.price.converted.toFixed(2)"
                        :value="item.price.converted.toFixed(2)"
                        readonly
                      />
                    </td>
                    <td class="input-cell font-weight-bold">
                      <input
                        class="input-number"
                        :placeholder="getTotalRate(item.quantity, item.price.converted).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        :value="getTotalRate(item.quantity, item.price.converted).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        readonly
                      />
                    </td>
                  </tr>
                </tbody>

                <!-- Discount & Rebate-->
                <tbody
                  class="line-item-single"
                  v-if="fc.hasOwnProperty(CONSTANT.TYPE_DISCOUNT_REBATE)"
                >
                  <tr class="body-2" v-for="(item, i) in fc[CONSTANT.TYPE_DISCOUNT_REBATE]">
                    <td></td>
                    <td class="description">
                      <input
                        type="text"
                        class="input-description"
                        @input="handleInput"
                        :placeholder="item.description"
                        v-model="item.description"
                        :readonly="!allowEditComponent"
                      />

                      <v-btn
                        x-small
                        icon
                        @click="onRemoveItem(item)"
                        style="background-color: #FF5252"
                        class="add-remove-item"
                      >
                        <v-icon size="18" color="white">remove</v-icon>
                      </v-btn>
                    </td>
                    <td>
                      <v-chip
                        v-if="$root.is_admin"
                        x-small
                        label
                        dark
                        :color="getCurrencyColor(item).color"
                        class="font-weight-bold caption px-1"
                        @click="onOpenConvertedcurrenciesDialog(item)"
                      >{{item.currency}}</v-chip>

                      <v-chip
                        v-else
                        x-small
                        label
                        dark
                        :color="getCurrencyColor(item).color"
                        class="font-weight-bold caption px-1"
                      >{{item.currency}}</v-chip>
                    </td>
                    <td v-if="allowEditComponent" class="input-cell">
                      <input
                        type="text"
                        class="caption"
                        @input="handleInput"
                        v-model="item.code"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td class="input-cell">
                      <input
                        type="number"
                        class="input-number"
                        :placeholder="item.quantity"
                        v-model.number="item.quantity"
                        @input="handleInput"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td class="input-cell">
                      <input
                        type="number"
                        class="input-number"
                        v-model.number="item.price.base"
                        :placeholder="item.price.base.toFixed(2)"
                        @input="handleBaseRateInput(item.price, item.conversion_factor)"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td class="input-cell">
                      <input
                        type="number"
                        class="input-number"
                        :placeholder="item.conversion_factor.toFixed(4)"
                        v-model.number="item.conversion_factor"
                        @input="handleBaseRateInput(item.price, item.conversion_factor)"
                        :readonly="!allowEditComponent"
                      />
                    </td>
                    <td class="input-cell">
                      <input
                        type="number"
                        class="input-number"
                        :placeholder="item.price.converted.toFixed(2)"
                        :value="item.price.converted.toFixed(2)"
                        readonly
                      />
                    </td>
                    <td class="input-cell font-weight-bold">
                      <input
                        class="input-number"
                        :placeholder="getTotalRate(item.quantity, item.price.converted).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        :value="getTotalRate(item.quantity, item.price.converted).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})"
                        readonly
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </v-row>

            <v-row justify="end" class="pt-4">
              <span class="font-weight-bold headline primary--text mr-10">TOTAL PRICE:</span>
              <span
                class="font-weight-bold headline primary--text"
              >{{price_total.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}}</span>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col v-else>
        <v-alert
          tile
          dense
          prominent
          border="left"
          style="z-index: 1;"
          type="info"
          class="my-4"
        >Click generate financials.</v-alert>
      </v-col>
    </v-row>
  </v-row>
</template>

<script>
import constant from "../../const"

export default {
  props: ["value", "quotation", "view", "editable"],
  components: {},
  data() {
    return {
      af_line_items: this.value,
      CONSTANT: constant,
      snackbarColor: "",
      showSnackbar: false,
      snackbarMsg: "",
      snackbarTimeout: 6000,

      convertedCurrenciesList: [],
      dialogOnConvertedCurrencies: false,

      selectedFCItem: null,

      portPrefixDisplayed: false,
      rateUnitSuffixDisplayed: false,
      clonedFC: {},
      fc_cloned: false,

      rate_unit_description_comp: [
        constant.TYPE_EXPORT_DGCLASS_CHARGES,
        constant.TYPE_EXPORT_CUSTOMS_CHARGES,
        constant.TYPE_EXPORT_LOCAL_CHARGES,
        constant.TYPE_EXPORT_HAULAGE_CHARGES,
        constant.TYPE_EXPORT_TRANSPORT_CHARGES,
        constant.TYPE_IMPORT_HAULAGE_CHARGES,
        constant.TYPE_IMPORT_TRANSPORT_CHARGES,
        constant.TYPE_IMPORT_DGCLASS_CHARGES,
        constant.TYPE_IMPORT_CUSTOMS_CHARGES,
        constant.TYPE_IMPORT_LOCAL_CHARGES,
        constant.TYPE_OCEAN_FREIGHT_CHARGES,
        constant.TYPE_AIR_FREIGHT_CHARGES,
      ],

      port_description_comp: [
        constant.TYPE_EXPORT_DGCLASS_CHARGES,
        constant.TYPE_EXPORT_CUSTOMS_CHARGES,
        constant.TYPE_EXPORT_LOCAL_CHARGES,
        constant.TYPE_EXPORT_HAULAGE_CHARGES,
        constant.TYPE_EXPORT_TRANSPORT_CHARGES,
        constant.TYPE_IMPORT_HAULAGE_CHARGES,
        constant.TYPE_IMPORT_TRANSPORT_CHARGES,
        constant.TYPE_IMPORT_DGCLASS_CHARGES,
        constant.TYPE_IMPORT_CUSTOMS_CHARGES,
        constant.TYPE_IMPORT_LOCAL_CHARGES,
      ],

      showInsuranceItems: true,
      showOceanFreightChargesItems: true,
      showAirFreightChargesItems: true,
      showExportCargoChargesTypeItems: true,
      showExportLocalChargesItems: true,
      showExportCustomsClearanceItems: true,
      showExportHaulageItems: true,
      showExportTransportItems: true,

      showImportCargoChargesTypeItems: true,
      showImportCustomsClearanceItems: true,
      showImportLocalChargesItems: true,
      showImportHaulageItems: true,
      showImportTransportItems: true,

      showOtherServices: true,
      showTaxesDuties: true,
      showMiscInvoice: true,

      addExportTransportGroupToInvoice: false,
      addExportLocalChargesGroupToInvoice: false,
      addExportCustomsClearanceGroupToInvoice: false,

      addImportCargoTypeChargesGroupToInvoice: false,
      addImportHaulageGroupToInvoice: false,
      addImportTransportGroupToInvoice: false,
      addImportLocalChargesGroupToInvoice: false,
      addImportCustomsClearanceGroupToInvoice: false,

      addOtherServiceGroupToInvoice: false,
    }
  },
  created() {
    // load converted currency list
    if (this.$root.is_admin) {
      this.loadConvertedCurrenciesList()
    }
  },

  mounted() {},

  watch: {
    value: function () {
      this.af_line_items = this.value
    },
  },

  methods: {
    handleInput(e) {
      if (this.allowEditComponent) {
        this.$emit("input", this.af_line_items)
        this.$emit("updated", true)
      }
    },

    handleBaseRateInput(financial_component, conversion_factor) {
      if (this.allowEditComponent) {
        financial_component.converted = parseFloat(
          (financial_component.base * conversion_factor).toFixed(2)
        )
        this.$emit("input", this.af_line_items)
        this.$emit("updated", true)
      }
    },

    /**
     * Prefix the port code into the description
     */
    prefixPortDescription() {
      // if FC is not cloned, then clone FC
      if (!this.fc_cloned) {
        this.clonedFC = JSON.parse(JSON.stringify(this.fc))
        this.fc_cloned = true
      }

      // loop through fc
      for (const key in this.fc) {
        if (this.fc.hasOwnProperty(key)) {
          if (this.port_description_comp.includes(key)) {
            const component = this.fc[key]

            component.forEach((item) => {
              item.description = `${this.getPortCodeText(key)} ${
                item.description
              }`
            })
          }
        }
      }

      this.portPrefixDisplayed = true
    },

    /**
     * Suffix rate per unit into the description
     */
    suffixRateUnitDescription() {
      // if FC is not cloned, then clone FC
      if (!this.fc_cloned) {
        this.clonedFC = JSON.parse(JSON.stringify(this.fc))
        this.fc_cloned = true
      }

      // loop through fc
      for (const key in this.fc) {
        if (this.fc.hasOwnProperty(key)) {
          if (this.rate_unit_description_comp.includes(key)) {
            const component = this.fc[key]

            component.forEach((item) => {
              item.description = `${
                item.description
              } ${this.getCurrencyUnitPriceText(item)}`
            })
          }
        }
      }

      this.rateUnitSuffixDisplayed = true
    },

    /**
     * Reset the description view
     */
    resetDescription() {
      this.fc = this.clonedFC
      this.fc_cloned = false
      this.portPrefixDisplayed = false
      this.rateUnitSuffixDisplayed = false
    },

    showSingleLineItem(showItem, financial_component) {
      return (
        financial_component != null &&
        (this.allowEditComponent || financial_component.quantity > 0) &&
        showItem
      )
    },

    showSubLineItem(expand, financial_component) {
      if (!expand) {
        return false
      }

      if (!this.allowEditComponent && financial_component.quantity == 0) {
        return false
      }

      return true
    },

    showGroupItem(financial_component) {
      if (!this.fc_valid) {
        return false
      }

      if (financial_component == null) {
        return false
      }

      if (financial_component.length == 0) {
        return false
      }

      if (!Array.isArray(financial_component)) {
        return false
      }

      for (var i = 0; i < financial_component.length; i++) {
        if (financial_component[i].quantity > 0) {
          return true
        }
      }

      return false
    },

    /**
     * calculate component price
     */
    getComponentPrice(component) {
      let total = 0.0
      // if fc is invalid, return 0.00
      if (!this.fc_valid) {
        return total
      }

      component.forEach((item) => {
        total += item.price.converted * item.quantity
      })
      return total
    },

    getTotalRate(quantity, rate) {
      return parseFloat((quantity * rate).toFixed(2))
    },

    getPortCodeText(financial_component) {
      let exportPatt = /export/g
      // let importPatt = /import/g;

      // if export
      if (exportPatt.test(financial_component)) {
        return `${this.quotation.origin_port.un_code}: `
      } else {
        return `${this.quotation.destination_port.un_code}: `
      }
    },

    getCurrencyUnitPriceText(financial_component) {
      if (financial_component == null) {
        return "(FC Error)"
      }

      var string = ""
      if (financial_component.currency != null) {
        // if min is used as base
        if (
          financial_component.price.min > 0 &&
          financial_component.price.min == financial_component.price.base
        ) {
          string = `(${financial_component.currency} ${financial_component.price.per_unit}`

          switch (financial_component.uom) {
            case "WM":
              string = string + " per WM"
              break
            case "RT":
              string = string + " per RT"
              break
            case "CW":
              string = string + " per CW"
              break
            case "CTR":
              string = string + " per container"
              break
            case "SET":
              string = string + " per shipment"
              break
            case "C3KG":
              string = string + " per 300KG, 1CBM:300KG"
              break
            case "QTL":
              string = string + " per 100KG"
              break
            default:
              string = string + ")"
          }

          string = `${string}. Min. ${financial_component.currency} ${financial_component.price.min})`
        } else {
          string = `(${financial_component.currency} ${financial_component.price.base}`

          switch (financial_component.uom) {
            case "WM":
              string = string + " per WM)"
              break
            case "RT":
              string = string + " per RT)"
              break
            case "CW":
              string = string + " per CW)"
              break
            case "CTR":
              string = string + " per container)"
              break
            case "SET":
              string = string + " per shipment)"
              break
            case "C3KG":
              string = string + " per 300KG, 1CBM:300KG)"
              break
            case "QTL":
              string = string + " per 100KG)"
              break
            default:
              string = string + ")"
          }
        }
      }

      return string
    },

    /**
     * Get the converted currency list
     */
    loadConvertedCurrenciesList() {
      this.$root.blockUImsg = "please wait..."
      this.$root.blockUIshow = true
      this.$https
        .get(`/api/currencies/${this.quotation.currency}/converted`)
        .then((resp) => {
          if (resp.data.status == constant.RESPONSE_STATUS_OK) {
            this.convertedCurrenciesList = resp.data.data
          } else {
            this.convertedCurrenciesList = []
            this.showSnackbar = true
            this.snackbarColor = "error"
            this.snackbarMsg = resp.data.msg
          }
          this.$root.blockUIshow = false
        })
        .catch((err) => {
          console.log(err)
          this.$root.blockUIshow = false
          this.showSnackbar = true
          this.snackbarColor = "error"
          this.snackbarMsg = err
        })
    },

    /**
     * on open dialog for converted currency
     */
    onOpenConvertedcurrenciesDialog(fc_item) {
      // clone selected fc item
      this.selectedFCItem = fc_item
      this.dialogOnConvertedCurrencies = true
    },

    /**
     * Update the converted currency rate to new rate
     */
    updateFCItemConverted(item) {
      this.selectedFCItem.currency = item.base_currency
      this.selectedFCItem.conversion_factor = item.conversion_rate

      // update the price
      this.selectedFCItem.price.converted = parseFloat(
        (
          this.selectedFCItem.price.base * this.selectedFCItem.conversion_factor
        ).toFixed(2)
      )
      // close dialog
      this.dialogOnConvertedCurrencies = false
    },

    /**
     * Get the currency color
     */
    getCurrencyColor(item) {
      var color = "black"
      switch (item.currency) {
        case "MYR":
          color = "primary"
          break
        case "USD":
          color = "green"
          break
        case "SGD":
          color = "red"
          break
        case "HKD":
          color = "orange"
          break
        case "THB":
          color = "purple"
          break
        case "EUR":
          color = "cyan darken-2"
          break
        case "AUD":
          color = "blue-grey"
          break
        case "CNY":
          color = "pink"
          break
        case "JPY":
          color = "indigo"
          break

        default:
          break
      }
      return { color: color }
    },

    /**
     * Add an item
     */
    onAddItem(type, code) {
      // insert the number code
      this.af_line_items.push({
        type: type,
        code: code,
        conversion_factor: 1.0,
        cost: {
          base: 0.0,
          min: 0.0,
          per_unit: 0.0,
          converted: 0.0,
        },
        currency: this.quotation.currency,
        description: null,
        price: {
          base: 0.0,
          min: 0.0,
          per_unit: 0.0,
          converted: 0.0,
        },
        quantity: 1,
        uom: "SET",
      })

      this.$emit("updated", true)
    },

    /**
     * Remove an item
     */
    onRemoveItem(item) {
      this.af_line_items = this.af_line_items.filter(function (component) {
        return component != item
      })
      this.$emit("input", this.af_line_items)
      this.$emit("updated", true)

      // for (var i = 0; i < fc_arr.length; i++) {
      //   if (fc_arr[i] == item) {
      //     fc_arr.splice(i, 1)
      //   }
      // }
    },
  },
  computed: {
    fc: {
      get() {
        if (this.af_line_items == null || this.af_line_items.length == 0) {
          return null
        }
        if (this.quotation == null) {
          return null
        }

        let ret = {}
        // group the fc components
        this.af_line_items.forEach((component) => {
          if (ret.hasOwnProperty(component.type)) {
            ret[component.type].push(component)
          } else {
            ret[component.type] = [component]
          }
        })

        return ret
      },
      set(value) {
        let ret = []

        for (const key in value) {
          if (value.hasOwnProperty(key)) {
            const component = value[key]
            component.forEach((element) => {
              ret.push(element)
            })
          }
        }
        this.af_line_items = ret
      },
    },

    fc_valid: {
      get() {
        return (
          this.fc != null &&
          this.view != null &&
          !constant.isObjectEmpty(this.fc)
        )
      },
    },

    price_total: {
      get() {
        let totalPrice = 0.0

        if (this.fc_valid) {
        }

        this.af_line_items.forEach((component) => {
          totalPrice += component.price.converted * component.quantity
        })

        return parseFloat(totalPrice.toFixed(2))
      },
    },

    allowEditComponent: {
      get() {
        return this.$root.is_admin && this.editable
      },
    },
  },
}
</script>

<style scoped>
table.financial-table tr {
  border: none;
}

.v-btn--icon.v-size--x-small.add-remove-item {
    height: 14px;
    width: 14px;
}
</style>